import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { authApi } from "@/api/services";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";
import { validatePassword, PASSWORD_HINT } from "@/lib/passwordPolicy";

/**
 * Consumes an org-member invitation token (`/accept-invite?token=...`). The
 * invitee sets their OWN password and activates the account — replacing the
 * old flow that emailed a plaintext temporary password. On mount it looks up
 * the invitation (email + org) to greet the invitee; on submit it calls
 * POST /auth/accept-invite/confirm and redirects to sign-in.
 */
function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [invite, setInvite] = useState<{ email: string; org_name: string | null } | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [checking, setChecking] = useState(!!token);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    authApi
      .getInviteInfo(token)
      .then((info) => active && setInvite(info))
      .catch((err: unknown) => {
        if (!active) return;
        const detail = (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail;
        setLookupError(detail || "This invitation link is invalid or has expired.");
      })
      .finally(() => active && setChecking(false));
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setIsLoading(true);
    try {
      await authApi.acceptInvite(token, password);
      setIsSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err as { message?: string })?.message;
      setError(
        detail ||
          "Could not accept the invitation. The link may have expired — ask your admin to re-invite you.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const backToSignIn = (
    <Link
      to="/login"
      className="font-medium text-brand-600 transition-colors hover:text-brand-700"
    >
      Back to sign in
    </Link>
  );

  if (!token || lookupError) {
    return (
      <AuthShell
        eyebrow="Invitation"
        title="This invitation link is invalid"
        subtitle={lookupError || "The link is missing its invitation token."}
        illustrationTagline="Ask your admin to re-invite you."
        footer={backToSignIn}
      >
        <p className="py-4 text-center text-sm text-slate-600 dark:text-slate-300">
          Open the most recent &ldquo;You&rsquo;ve been invited to FTL&rdquo;
          email and use its link, or ask your admin to send a fresh invitation.
        </p>
      </AuthShell>
    );
  }

  if (isSuccess) {
    return (
      <AuthShell
        eyebrow="Welcome"
        title="You're all set"
        subtitle="Your account is active — sign in with your new password."
        illustrationTagline="Welcome to FTL."
        footer={backToSignIn}
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 shadow-[var(--shadow-glow)] dark:bg-brand-900/60 dark:text-brand-300">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Redirecting you to sign in&hellip;
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Invitation"
      title={
        invite?.org_name
          ? `Join ${invite.org_name}`
          : "Accept your invitation"
      }
      subtitle={
        checking
          ? "Loading your invitation…"
          : invite?.email
            ? `Set a password for ${invite.email} to activate your account.`
            : "Set a strong password with at least 12 characters to activate your account."
      }
      illustrationTagline="Set your password to get started."
      footer={backToSignIn}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="invite-password">Create password</Label>
          <Input
            id="invite-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            placeholder="At least 12 characters"
          />
          <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-confirm">Confirm password</Label>
          <Input
            id="invite-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={isLoading || checking}>
          {isLoading ? "Activating…" : "Activate account"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default AcceptInvite;
